'use client';

import { ChevronLeftIcon } from '@/components/icons/chevron-left-icon';
import { ChevronRightIcon } from '@/components/icons/chevron-right-icon';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { DataTest } from '@/types/data-test';
import useEmblaCarousel, {
  type UseEmblaCarouselType,
} from 'embla-carousel-react';
import WheelGesturesPlugin from 'embla-carousel-wheel-gestures';
import * as React from 'react';

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

type CarouselProps = {
  opts?: CarouselOptions;
  orientation?: 'horizontal' | 'vertical';
  plugins?: CarouselPlugin;
  setApi?: (api: CarouselApi) => void;
  visibleItems?: number;
};

type CarouselContextProps = {
  api: ReturnType<typeof useEmblaCarousel>[1];
  canScrollNext: boolean;
  canScrollPrev: boolean;
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  scrollNext: () => void;
  scrollPrev: () => void;
  visibleItems?: number;
} & CarouselProps;

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

const useCarousel = (): CarouselContextProps => {
  const context = React.useContext(CarouselContext);

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />');
  }

  return context;
};

const Carousel = ({
  children,
  className,
  opts,
  orientation = 'horizontal',
  plugins,
  setApi,
  visibleItems,
  ...props
}: React.ComponentProps<'div'> & CarouselProps): React.JSX.Element => {
  const [carouselRef, api] = useEmblaCarousel(
    {
      ...opts,
      axis: orientation === 'horizontal' ? 'x' : 'y',
    },
    [...plugins || [], WheelGesturesPlugin()]
  );
  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);

  const onSelect = React.useCallback((api: CarouselApi) => {
    if (!api) return;
    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
  }, []);

  const scrollPrev = React.useCallback(() => {
    api?.scrollPrev();
  }, [api]);

  const scrollNext = React.useCallback(() => {
    api?.scrollNext();
  }, [api]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        scrollPrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        scrollNext();
      }
    },
    [scrollPrev, scrollNext]
  );

  React.useEffect(() => {
    if (!api || !setApi) return;
    setApi(api);
  }, [api, setApi]);

  React.useEffect(() => {
    if (!api) return;
    onSelect(api);
    api.on('reInit', onSelect);
    api.on('select', onSelect);

    return (): void => {
      api?.off('select', onSelect);
    };
  }, [api, onSelect]);

  return (
    <CarouselContext.Provider value={{
      api,
      canScrollNext,
      canScrollPrev,
      carouselRef,
      opts,
      orientation:
          orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
      scrollNext,
      scrollPrev,
      visibleItems,
    }}
    >
      <div aria-roledescription="carousel"
        className={cn('relative', className)}
        data-slot="carousel"
        role="region"
        onKeyDownCapture={handleKeyDown}
        {...props}
      >
        {children}
      </div>
    </CarouselContext.Provider>
  );
};

const CarouselContent = ({ className, ...props }: React.ComponentProps<'div'>): React.JSX.Element => {
  const { carouselRef, orientation } = useCarousel();

  return (
    <div ref={carouselRef}
      className="overflow-hidden"
      data-slot="carousel-content"
    >
      <div className={cn('flex items-stretch gap-ms', orientation === 'horizontal' ? '' : '-mt-4 flex-col', className)}
        {...props}
      />
    </div>
  );
};

const CarouselItem = ({ className, ...props }: React.ComponentProps<'div'>): React.JSX.Element => {
  const { orientation, visibleItems } = useCarousel();

  const basis = visibleItems && visibleItems > 1 ? `[${100 / visibleItems}%]` : 'full';

  return (
    <div aria-roledescription="slide"
      className={cn(
        `min-w-0 shrink-0 grow-0 basis-${basis}`,
        orientation === 'horizontal' ? '' : 'pt-4',
        className
      )}
      data-slot="carousel-item"
      role="group"
      {...props}
    />
  );
};

const CarouselPrevious = ({
  className,
  dataTest,
  iconFontSizeClassName = '',
  size = 'icon',
  variant = 'outline',
  ...props
}: React.ComponentProps<typeof Button> & {
  iconFontSizeClassName?: string;
} & DataTest): React.JSX.Element => {
  const { canScrollPrev, orientation, scrollPrev } = useCarousel();

  return (
    <Button className={cn(
      'absolute size-12 bg-white border-0 hover:bg-white text-primary hover:text-primary shadow-xl hover:shadow-3xl',
      orientation === 'horizontal'
        ? 'top-1/2 -translate-y-1/2'
        : '-top-12 left-1/2 -translate-x-1/2 rotate-90',
      className
    )}
    data-slot="carousel-previous"
    data-test={dataTest}
    disabled={!canScrollPrev}
    size={size}
    variant={variant}
    onClick={scrollPrev}
    {...props}
    >
      <ChevronLeftIcon className={iconFontSizeClassName} />
      <span className="sr-only">Previous slide</span>
    </Button>
  );
};

const CarouselNext = ({
  className,
  dataTest,
  iconFontSizeClassName = '',
  size = 'icon',
  variant = 'outline',
  ...props
}: React.ComponentProps<typeof Button> & {
  iconFontSizeClassName?: string;
} & DataTest): React.JSX.Element => {
  const { canScrollNext, orientation, scrollNext } = useCarousel();

  return (
    <Button className={cn(
      'absolute size-12 bg-white border-0 hover:bg-white text-primary hover:text-primary shadow-xl hover:shadow-3xl',
      orientation === 'horizontal'
        ? 'top-1/2 right-0 -translate-y-1/2'
        : '-bottom-12 left-1/2 -translate-x-1/2 rotate-90',
      className
    )}
    data-slot="carousel-next"
    data-test={dataTest}
    disabled={!canScrollNext}
    size={size}
    variant={variant}
    onClick={scrollNext}
    {...props}
    >
      <ChevronRightIcon className={iconFontSizeClassName} />
      <span className="sr-only">Next slide</span>
    </Button>
  );
};

export {
  Carousel, type CarouselApi, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious,
};
